import React from "react";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import FraudPlayersTable from "./FraudPlayersTable";
import {
  selectBizBeAwareFraudPlayersLoadedRequestKey,
  selectBizBeAwareFraudPlayersLoading,
  selectBizBeAwareFraudPlayersOutput,
} from "../../../../../../../features/bizDevelopmentDashboardSlice";
import { buildBeAwareRequestKey } from "../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardEndPointLogic";
import { useBizDevelopmentDashboard } from "../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { exportBeAwareTableToCSV } from "../../beAwareCsvHelpers";

export interface BeAwareTableRef {
  refresh: () => void;
}

const tableHeadings = {
  brand_name: "Brand Name",
  brand_id: "Brand ID",
  total_income: "Total Income",
  cpa: "CPA",
  rs: "RS",
  cpa_from_total_income: "% CPA from Total Income",
  rs_from_total_income: "% RS from Total Income",
  qftd: "QFTD",
  ftd: "FTD",
};

// Keep raw values; empty string when missing so the table shows "N/A".
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);

// Round monetary values to 2 decimals; empty string when missing/non-numeric.
const money = (value: any) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : String(Math.round(n * 100) / 100);
};

// Monetary columns shown with a "$" icon in the table; prefixed with "$" in CSV.
const MONEY_KEYS = new Set(["total_income", "cpa", "rs"]);

// handles negative numbers and adds thousand separators
const formatMoney = (value: number | string) => {
  const num = Number(value);
  const isNegative = num < 0;
  const formatted = Math.abs(num).toLocaleString();
  return `${isNegative ? "-" : ""}$${formatted}`;
};

// CSV cell resolver: prefix monetary columns with "$" to match the table.
const getFraudPlayersCellValue = (key: string, row: any) => {
  const value = row[key] ?? "";
  return MONEY_KEYS.has(key) && value !== "" ? formatMoney(value) : value;
};

// Calculated in client: (numerator / denominator) * 100, rounded to a whole
// number and suffixed with "%" (e.g. 108.73 -> "109%").
const percentOf = (numerator: any, denominator: any) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return "";
  return `${Math.round((n / d) * 100)}%`;
};

const FraudPlayers = React.forwardRef<BeAwareTableRef>((_, ref) => {
  const isLoading = useSelector(selectBizBeAwareFraudPlayersLoading);
  const output = useSelector(selectBizBeAwareFraudPlayersOutput);
  const loadedRequestKey = useSelector(
    selectBizBeAwareFraudPlayersLoadedRequestKey
  );
  const { fetchBeAwareFraudPlayersData, getSitesFromParams } =
    useBizDevelopmentDashboard();

  React.useEffect(() => {
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    if (loadedRequestKey === buildBeAwareRequestKey(sites)) return;
    fetchBeAwareFraudPlayersData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchBeAwareFraudPlayersData(getSitesFromParams());
    },
  }));

  const rows = React.useMemo(
    () =>
      (output ?? []).map((item: Record<string, any>) => ({
        brand_name: raw(item.brand_display_name),
        brand_id: raw(item.brand_id),
        total_income: money(item.total_income),
        cpa: money(item.cpa),
        rs: money(item.revshare),
        cpa_from_total_income: percentOf(item.cpa, item.total_income),
        rs_from_total_income: percentOf(item.revshare, item.total_income),
        qftd: raw(item.qftd),
        ftd: raw(item.ftd),
      })),
    [output]
  );

  const [selectedBrand, setSelectedBrand] = React.useState<string | null>(null);

  // Unique, non-empty brand names used as the "Brand Name" search options.
  const brandOptions = React.useMemo(
    () =>
      (
        Array.from(
          new Set(
            rows
              .map((row: Record<string, any>) => row.brand_name)
              .filter((name: string) => name && name.trim() !== "")
          )
        ) as string[]
      ).sort(),
    [rows]
  );

  // Drop a stale selection when refreshed data no longer contains that brand.
  React.useEffect(() => {
    if (selectedBrand && !brandOptions.includes(selectedBrand)) {
      setSelectedBrand(null);
    }
  }, [brandOptions, selectedBrand]);

  // Rows shown in the table (and exported) respect the brand filter.
  const filteredRows = React.useMemo(
    () =>
      selectedBrand
        ? rows.filter(
            (row: Record<string, any>) => row.brand_name === selectedBrand
          )
        : rows,
    [rows, selectedBrand]
  );

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows: filteredRows,
      filename: "fraud_players",
      getCellValue: getFraudPlayersCellValue,
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2} alignItems="center" px={2} pb={2}>
          <Grid size={6}>
            <Typography fontSize={"18px"} mr={2}>
              Fraud Players in MTD
            </Typography>
          </Grid>
          <Grid size={6}>
            <Box
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
            >
              <Autocomplete
                size="small"
                options={brandOptions}
                value={selectedBrand}
                onChange={(_, newValue) => setSelectedBrand(newValue)}
                disabled={isLoading}
                sx={{ width: 260 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand Name"
                    color="secondary"
                    slotProps={{ inputLabel: { shrink: true } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <IconButton
                aria-label="download"
                color="secondary"
                sx={{ ml: 2 }}
                disabled={isLoading || filteredRows.length === 0}
                onClick={handleDownload}
                data-tracker="857"
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <FraudPlayersTable
        tableHeadings={tableHeadings}
        rows={filteredRows}
        isLoading={isLoading}
        filterKey={selectedBrand ?? ""}
      />
    </>
  );
});

export default FraudPlayers;
